import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "../components/ErrorNote";
import { BookForPatient } from "../components/BookForPatient";
import { MoveAppointment } from "../components/MoveAppointment";

type Booking = {
  id: string;
  slot_id: string;
  staff_id: string;
  staff_name: string;
  patient_id: string;
  patient_name: string;
  status: "booked" | "cancelled" | "completed" | "no_show";
  reason: string;
  source: "patient" | "staff" | "ai_chat";
  start_datetime: string;
  end_datetime: string;
};

const STATUS_LABELS: Record<Booking["status"], string> = {
  booked: "Booked",
  cancelled: "Cancelled",
  completed: "Attended",
  no_show: "Did not attend",
};

const SOURCE_LABELS: Record<Booking["source"], string> = {
  patient: "In the app",
  staff: "By the clinic",
  ai_chat: "In chat",
};

function today() {
  // The browser's own day. A clinic and the person at its desk share a zone, so
  // this and the server's clinic-local day are the same day.
  return new Date().toLocaleDateString("en-CA");
}

function shift(day: string, days: number) {
  const date = new Date(`${day}T12:00:00`);
  date.setDate(date.getDate() + days);
  return date.toLocaleDateString("en-CA");
}

function clock(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** The seven days the strip shows, starting three before the chosen one. */
function weekAround(day: string) {
  return Array.from({ length: 7 }, (_, i) => shift(day, i - 3));
}

function dayName(day: string) {
  return new Date(`${day}T12:00:00`).toLocaleDateString([], { weekday: "short" });
}

function dayNumber(day: string) {
  return new Date(`${day}T12:00:00`).getDate();
}

/**
 * The clinic's day.
 *
 * Attendance is the thing this screen exists for, and the thing a busy clinic
 * drops. An appointment nobody marks is neither attended nor missed, and the
 * no-show number the PRD asks for comes from the difference. The sweep that
 * flags the unmarked ones is off unless a clinic turns it on, because a practice
 * running half an hour late would otherwise mark honest patients absent
 * (SCH-F-032).
 *
 * The reason is on the row. It is the patient's own words about why they are
 * coming, written by them or taken down at the desk, and it is not a clinical
 * record. A diagnosis never appears here.
 */
export function Appointments() {
  const [day, setDay] = useState(today());
  const [rows, setRows] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [moving, setMoving] = useState<Booking | null>(null);
  const [booking, setBooking] = useState(false);
  /** Open slots per day, for the strip. Keyed by the clinic-local date. */
  const [free, setFree] = useState<Record<string, number>>({});

  const week = weekAround(day);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: failed } = await api.GET("/portal/appointments", {
      params: { header: clinicHeader(), query: { date: day } },
    });
    setLoading(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setRows((data?.items ?? []) as Booking[]);
  }, [day]);

  useEffect(() => {
    void load();
  }, [load]);

  /* How full each day of the strip is.
   *
   * The desk answers "is Thursday full?" while somebody waits on the
   * telephone, and the only way to find out was to open Thursday, go back, and
   * try Friday. These are real open slots from the scheduling data, counted by
   * day, and never an estimate.
   *
   * Fetched from the first day of the strip. The limit caps what comes back,
   * so a day beyond the last slot returned is shown as unknown rather than as
   * zero: an empty count and "we did not look that far" are different answers
   * and only one of them is safe to act on. */
  const countFree = useCallback(async () => {
    const first = week[0] ?? day;
    const { data, error: failed } = await api.GET("/portal/slots", {
      params: { header: clinicHeader(), query: { from: first, limit: 500 } },
    });
    if (failed || !data?.items) return;

    const counts: Record<string, number> = {};
    let furthest: string = first;
    for (const slot of data.items as { start_datetime: string }[]) {
      const key = new Date(slot.start_datetime).toLocaleDateString("en-CA");
      counts[key] = (counts[key] ?? 0) + 1;
      if (key > furthest) furthest = key;
    }
    // Days past the last slot we were given are unknown, not empty.
    for (const d of week) {
      if (d <= furthest && counts[d] === undefined) counts[d] = 0;
    }
    setFree(counts);
    // `week` is derived from `day`, so this re-runs when the day moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  useEffect(() => {
    void countFree();
  }, [countFree]);

  async function mark(booking: Booking, status: "completed" | "no_show") {
    setBusy(booking.id);
    const { error: failed } = await api.POST("/portal/appointments/{id}/attendance", {
      params: { header: clinicHeader(), path: { id: booking.id } },
      body: { status },
    });
    setBusy(null);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    await load();
  }

  async function cancel(booking: Booking) {
    const reason = window.prompt(
      `Cancel ${booking.patient_name} at ${clock(booking.start_datetime)}?\n\n` +
        "Say why, so whoever picks this up knows what happened.",
    );
    if (reason === null) return;

    setBusy(booking.id);
    const { error: failed } = await api.DELETE("/portal/appointments/{id}", {
      params: { header: clinicHeader(), path: { id: booking.id } },
      body: { reason },
    });
    setBusy(null);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    await load();
  }

  const live = rows.filter((row) => row.status === "booked");
  const unmarked = live.filter((row) => new Date(row.end_datetime) < new Date());

  return (
    <section>
      {/* The band, then the day. Its board puts the title on the photograph
          and the date stepper in the working area underneath, which is the
          right way round: the title never changes and the date is the control
          somebody actually uses. */}
      <header className="screen-band">
        <div className="screen-band-photo" aria-hidden="true" />
        <div className="screen-band-words">
          <h1>Appointments</h1>
          <p className="screen-lead">
            One day&rsquo;s bookings, and the way to book somebody in.
          </p>
        </div>
      </header>

      <div className="row-between">
        <div className="day-picker">
          <button type="button" className="link" onClick={() => setDay(shift(day, -1))}>
            ‹ Previous
          </button>
          <input
            type="date"
            value={day}
            onChange={(event) => setDay(event.target.value || today())}
          />
          <button type="button" className="link" onClick={() => setDay(shift(day, 1))}>
            Next ›
          </button>
          {day !== today() && (
            <button type="button" className="link" onClick={() => setDay(today())}>
              Today
            </button>
          )}
        </div>
        <button type="button" className="primary" onClick={() => setBooking(true)}>
          Book someone in
        </button>
      </div>

      {/* How full each day is, without opening it. This is the question the
          desk is answering while somebody waits on the telephone, and until
          now the only way to answer it was to open a day, go back, and try the
          next one. */}
      <ul className="week-strip">
        {week.map((d) => {
          const open = free[d];
          // A day that has been and gone has no open slots, and saying "full"
          // about it is a different claim from the true one. Nobody books into
          // yesterday, so it carries no count at all.
          const past = d < today();
          const label = past
            ? "past"
            : open === undefined
              ? "—"
              : open === 0
                ? "full"
                : `${open} free`;
          return (
            <li key={d}>
              <button
                type="button"
                className={`week-day${d === day ? " on" : ""}${past ? " past" : ""}`}
                aria-current={d === day ? "date" : undefined}
                onClick={() => setDay(d)}
              >
                <span className="week-name">{dayName(d)}</span>
                <span className="week-number">{dayNumber(d)}</span>
                <span
                  className={!past && open === 0 ? "week-free none" : "week-free"}
                >
                  {label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <ErrorNote message={error} />

      {unmarked.length > 0 && (
        // Not a warning. A clinic running late has unmarked appointments and
        // nothing is wrong. It is a reminder that the list is not finished.
        <p className="note">
          {unmarked.length} {unmarked.length === 1 ? "appointment has" : "appointments have"}{" "}
          finished without an answer. Mark who came, so the clinic's own numbers
          mean something.
        </p>
      )}

      {loading && rows.length === 0 && <p className="empty">Reading the day…</p>}

      {!loading && rows.length === 0 && (
        <p className="empty">Nothing booked for this day.</p>
      )}

      {rows.length > 0 && (
        <div className="data-table-wrap">
          <table className="data-table records">
          <thead>
            <tr>
              <th>Time</th>
              <th>Patient</th>
              <th>Doctor</th>
              <th>Reason</th>
              <th>Booked</th>
              <th>Came?</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={row.status === "cancelled" ? "muted" : undefined}>
                <td>{clock(row.start_datetime)}</td>
                <td>
                  <Link to={`/patients/${row.patient_id}`}>{row.patient_name}</Link>
                </td>
                <td>{row.staff_name}</td>
                <td>{row.reason || <span className="quiet">not given</span>}</td>
                <td className="quiet">{SOURCE_LABELS[row.source]}</td>
                <td>
                  {row.status === "booked" ? (
                    <span className="actions">
                      <button
                        type="button"
                        className="link"
                        disabled={busy === row.id}
                        onClick={() => void mark(row, "completed")}
                      >
                        Attended
                      </button>
                      <button
                        type="button"
                        className="link"
                        disabled={busy === row.id}
                        onClick={() => void mark(row, "no_show")}
                      >
                        Did not
                      </button>
                      <button
                        type="button"
                        className="link"
                        disabled={busy === row.id}
                        onClick={() => setMoving(row)}
                      >
                        Move
                      </button>
                      <button
                        type="button"
                        className="link danger"
                        disabled={busy === row.id}
                        onClick={() => void cancel(row)}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <span className="quiet">{STATUS_LABELS[row.status]}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {booking && (
        <BookForPatient
          onClose={() => setBooking(false)}
          onBooked={() => {
            setBooking(false);
            void load();
          }}
        />
      )}

      {moving && (
        <MoveAppointment
          appointmentId={moving.id}
          patientName={moving.patient_name}
          staffId={moving.staff_id}
          was={moving.start_datetime}
          onClose={() => setMoving(null)}
          onMoved={() => {
            setMoving(null);
            void load();
          }}
        />
      )}
    </section>
  );
}
